import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from '@remix-run/react';
import type { TPostActionsElement } from '@shared/dream-components';
import { PostActionsElement } from '@shared/dream-components';
import cx from 'classnames';

import PostLikeAction from '~/components/PostLikeAction';
import { usePostDataContext } from '~/context/usePostDataContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import usePostInsights from '~/hooks/useLoaders/usePostInsights';
import useScrollDetection from '~/hooks/useScrollDetection';

interface Props {
  node: TPostActionsElement;
  children: ReactNode | ReactNode[];
}

const PostActions = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const { post } = usePostDataContext();
  const postInsights = usePostInsights(post.id);
  const { canShowCommentsNode, showMeteredUsageBanner, showPaywallAndHidePostContent } = usePostPageContext() || {
    canShowCommentsNode: false,
    showMeteredUsageBanner: false,
    showPaywallAndHidePostContent: false,
  };

  const navigate = useNavigate();
  const location = useLocation();

  const { canScrollVertically, hasVerticalScrolling } = useScrollDetection({
    delay: 500,
  });

  // Offset the footer upward by the banner height so the fixed banner (bottom-0, z-40)
  // sits visually above the footer. This keeps them independent so the footer can
  // slide out via translate without taking the banner with it.
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    if (!showMeteredUsageBanner) {
      setBannerHeight(0);
      return;
    }
    const el = document.getElementById('metered-paywall-usage-banner');
    if (!el) {
      setBannerHeight(0);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const bannerH = Math.ceil(entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height);
        // Subtract the banner's border-top so the footer's background sits flush against
        // the banner's colored area rather than against the top of its border.
        const bannerBorderTop = Number.parseFloat(getComputedStyle(el).borderTopWidth) || 0;
        setBannerHeight(bannerH - bannerBorderTop);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showMeteredUsageBanner]);

  const handleClickComment = () => {
    try {
      if (typeof document !== 'undefined') {
        const commentElement = document.querySelector('#comments');
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
    } catch {
      // do nothing
    }

    // Fallback
    navigate(`${location.pathname}#comments`);
  };

  const handleClickBack = () => {
    navigate('/');
  };

  const shareUrl = typeof document !== 'undefined' ? document.location.href : '#';

  if (node?.attrs?.isEnabled === false || showPaywallAndHidePostContent) {
    return null;
  }

  return (
    <div
      className={cx(
        'fixed z-30 w-full transition-transform',
        hasVerticalScrolling && !canScrollVertically ? 'translate-y-full' : 'translate-y-0',
      )}
      style={{ bottom: bannerHeight }}
    >
      <PostLikeAction areLikesFetched={!postInsights.isLoading} likes={postInsights.likes} postId={post.id}>
        {({ isLiked, likesCount }) => (
          <PostActionsElement
            element={{
              ...node,
              attrs: {
                ...node.attrs,
                borderBottomWidth: '0',
                data: {
                  commentsCount: postInsights.comment_count,
                  likesCount: likesCount,
                  isLiked,
                  share: {
                    url: shareUrl,
                    title: post.web_title,
                    description: post.web_subtitle || '',
                  },
                },
              },
            }}
            onCommentClick={handleClickComment}
            onBackClick={handleClickBack}
            showCommentsButton={canShowCommentsNode}
            isLive
            translations={{
              'search.pagination.back': t('search.pagination.back'),
              'search.pagination.like': t('search.pagination.like'),
              'search.pagination.comment': t('search.pagination.comment'),
              'search.pagination.share': t('search.pagination.share'),
              'search.pagination.copy_link': t('search.pagination.copy_link'),
              'clipboard.link_copied': t('clipboard.link_copied'),
            }}
          >
            {children}
          </PostActionsElement>
        )}
      </PostLikeAction>
    </div>
  );
};

export { PostActions };
